import React, {useMemo} from 'react'
import {Box, Chip, Typography} from "@mui/material"
import {DataGrid, GridColDef, GridRenderCellParams} from "@mui/x-data-grid"
import {CompletionRequestJson} from "../../api/json/CompletionRequestJson"
import {TimeUtil} from "../../library/TimeUtil"

interface CompletionsTableProps {
    requests: CompletionRequestJson[]
    page: number
    pageSize: number
    rowCount: number
    loading: boolean
    onPageChange: (newPage: number) => void
    onRowClick: (request: CompletionRequestJson) => void
}

type StatusKey = 'succeeded' | 'failed' | 'running' | 'queued'

function resolveStatus(request: CompletionRequestJson): { key: StatusKey, label: string, color: 'success' | 'error' | 'warning' | 'default' } {
    if (request.succeeded) return {key: 'succeeded', label: 'Success', color: 'success'}
    if (request.failed) return {key: 'failed', label: 'Failed', color: 'error'}
    if (request.started) return {key: 'running', label: 'Running', color: 'warning'}
    return {key: 'queued', label: 'Queued', color: 'default'}
}

function formatLatency(ms: number | null): string {
    if (ms == null) return ""
    if (ms < 1000) return `${ms} ms`
    return `${(ms / 1000).toFixed(1)} s`
}

function CompletionsTable({requests, page, pageSize, rowCount, loading, onPageChange, onRowClick}: CompletionsTableProps) {
    const columns: GridColDef<CompletionRequestJson>[] = useMemo(() => [
        {
            field: 'created',
            headerName: 'Created',
            width: 140,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: CompletionRequestJson) => row.created,
            renderCell: (params: GridRenderCellParams<CompletionRequestJson, string>) => (
                <Typography variant="body2" color="text.secondary">
                    {TimeUtil.ago(params.value ?? "")}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 110,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<CompletionRequestJson>) => {
                const status = resolveStatus(params.row)
                return (
                    <Chip size="small"
                          variant="outlined"
                          color={status.color}
                          label={status.label}/>
                )
            },
        },
        {
            field: 'model',
            headerName: 'Model',
            width: 160,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<CompletionRequestJson>) => (
                <Typography variant="body2" noWrap>{params.row.model.name}</Typography>
            ),
        },
        {
            field: 'inputs',
            headerName: 'Input',
            flex: 1,
            minWidth: 200,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: CompletionRequestJson) =>
                Object.values(row.inputs ?? {}).join(' · '),
            renderCell: (params: GridRenderCellParams<CompletionRequestJson, string>) => (
                <Typography variant="body2" noWrap sx={{color: 'text.secondary'}}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'output',
            headerName: 'Output',
            flex: 1,
            minWidth: 200,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: CompletionRequestJson) =>
                row.results?.[0]?.text ?? "",
            renderCell: (params: GridRenderCellParams<CompletionRequestJson, string>) => (
                <Typography variant="body2" noWrap>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'user',
            headerName: 'User',
            width: 140,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<CompletionRequestJson>) => (
                <Typography variant="body2"
                            noWrap
                            color={params.row.user ? 'text.primary' : 'text.secondary'}>
                    {params.row.user?.fullName ?? '—'}
                </Typography>
            ),
        },
        {
            field: 'latency',
            headerName: 'Latency',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params: GridRenderCellParams<CompletionRequestJson>) => (
                <Typography variant="body2" color="text.secondary">
                    {formatLatency(params.row.latency)}
                </Typography>
            ),
        },
    ], [])

    return (
        <Box sx={{width: '100%'}}>
            <DataGrid
                autoHeight
                rows={requests}
                columns={columns}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                disableColumnMenu
                loading={loading}
                paginationMode="server"
                paginationModel={{page, pageSize}}
                rowCount={rowCount}
                pageSizeOptions={[pageSize]}
                onPaginationModelChange={(model) => onPageChange(model.page)}
                onRowClick={(params) => onRowClick(params.row as CompletionRequestJson)}
                sx={{
                    backgroundColor: 'white',
                    '& .MuiDataGrid-row': {cursor: 'pointer'},
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                }}
            />
        </Box>
    )
}

export default CompletionsTable
